import { motion } from "framer-motion";
import { MessageCircle, Zap, ShieldCheck } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import founderImg from "@/assets/founder-nick-enhanced.jpg";
import SectionGlow from "./SectionGlow";
import { ease, duration } from "@/lib/motion";

const FounderSection = () => {
  const isMobile = useIsMobile();

  return (
    <section
      id="founder"
      className="lg:sticky top-0 min-h-0 lg:min-h-[100dvh] flex items-center bg-charcoal relative overflow-hidden lg:pt-[var(--navbar-height,4.75rem)]"
      style={{ zIndex: 7 }}
      data-dark-section
    >
      <SectionGlow placement="top-right" />

      <div className="container mx-auto relative z-10 py-16 md:py-20 lg:py-0">
        <motion.div
          initial={{ opacity: 0, y: isMobile ? 0 : 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: duration.smooth, ease: ease.smooth }}
          className="max-w-4xl mx-auto grid md:grid-cols-[auto_1fr] gap-8 md:gap-14 items-center"
        >
          <div className="mx-auto md:mx-0">
            <div className="w-48 h-64 md:w-64 md:h-80 rounded-2xl overflow-hidden border border-cream/10 shadow-xl shadow-black/20">
              <img
                src={founderImg}
                alt="Nick, Founder of Greenleaf Digital"
                width={256}
                height={320}
                className="w-full h-full object-cover object-top"
                loading="eager"
                decoding="async"
                fetchPriority="low"
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage/90 mb-2 md:text-sm">Founder's Note</p>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-cream mb-4 font-bold leading-[1.1]">
              You work directly with <span className="text-accent-sweep">Nick</span>.
            </h2>
            <p className="text-sm text-cream/60 leading-relaxed mb-6 md:text-base max-w-lg">
              I've been designing and building websites for a few years now. Today I work with home service pros across North Carolina. Roofers, HVAC, landscapers, remodelers. Same person on the call, the design, and the launch. No account managers, no handoffs. Every project comes with a <span className="text-cream/80 font-medium">100% satisfaction guarantee</span>. If you're not happy, I keep refining.
            </p>
            <div className="flex flex-wrap gap-5">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-sage/10 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-sage" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-cream">7-Day</p>
                  <p className="text-[11px] text-cream/60">Delivery</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-sage/10 flex items-center justify-center">
                  <MessageCircle className="h-4 w-4 text-sage" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-cream">Direct Line</p>
                  <p className="text-[11px] text-cream/60">No middlemen</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-sage/10 flex items-center justify-center">
                  <ShieldCheck className="h-4 w-4 text-sage" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-cream">Satisfaction</p>
                  <p className="text-[11px] text-cream/60">Guaranteed</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FounderSection;
